import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MailIcon, PhoneIcon, ArrowLeftIcon, ArrowRightIcon, XIcon, UserIcon, ShieldCheckIcon, CameraIcon, CheckCircle2Icon, ChevronDownIcon, CheckIcon, MessageCircleIcon } from 'lucide-react';
import { Logo } from './Logo';
type Step = 'method' | 'email' | 'phone' | 'otp' | 'info' | 'id_scan';
type Country = {
  code: string;
  name: string;
  dial: string;
  flag: string;
  available: boolean;
};
// Real flag images (flagcdn) — flag emoji don't render on all platforms.
const flagUrl = (code: string) => `https://flagcdn.com/w80/${code.toLowerCase()}.png`;
const COUNTRIES: Country[] = [{
  code: 'AE',
  name: 'United Arab Emirates',
  dial: '+971',
  flag: flagUrl('AE'),
  available: true
}, {
  code: 'SA',
  name: 'Saudi Arabia',
  dial: '+966',
  flag: flagUrl('SA'),
  available: false
}, {
  code: 'QA',
  name: 'Qatar',
  dial: '+974',
  flag: flagUrl('QA'),
  available: false
}, {
  code: 'KW',
  name: 'Kuwait',
  dial: '+965',
  flag: flagUrl('KW'),
  available: false
}, {
  code: 'OM',
  name: 'Oman',
  dial: '+968',
  flag: flagUrl('OM'),
  available: false
}, {
  code: 'BH',
  name: 'Bahrain',
  dial: '+973',
  flag: flagUrl('BH'),
  available: false
}, {
  code: 'EG',
  name: 'Egypt',
  dial: '+20',
  flag: flagUrl('EG'),
  available: false
}];
const DEFAULT_COUNTRY = COUNTRIES[0];
interface LoginProps {
  onLogin: (verified?: boolean) => void;
  onClose?: () => void;
}
function GoogleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1817">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" data-id="element-1818" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" data-id="element-1819" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" data-id="element-1820" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" data-id="element-1821" />
    </svg>;
}
function AppleMark() {
  return <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-1822">
      <path d="M16.36 12.78c-.02-2.05 1.67-3.03 1.75-3.08-.95-1.39-2.43-1.58-2.96-1.6-1.26-.13-2.46.74-3.1.74-.64 0-1.62-.72-2.67-.7-1.37.02-2.64.8-3.35 2.03-1.43 2.48-.37 6.15 1.02 8.16.68.98 1.49 2.08 2.55 2.04 1.02-.04 1.41-.66 2.65-.66 1.23 0 1.58.66 2.66.64 1.1-.02 1.79-1 2.46-1.99.78-1.14 1.1-2.24 1.12-2.3-.02-.01-2.15-.83-2.18-3.27zM14.3 6.58c.56-.68.94-1.62.84-2.58-.81.03-1.79.54-2.37 1.22-.52.6-.98 1.56-.86 2.48.9.07 1.83-.46 2.39-1.12z" data-id="element-1823" />
    </svg>;
}
export function Login({
  onLogin,
  onClose
}: LoginProps) {
  const [step, setStep] = useState<Step>('method');
  const [authMethod, setAuthMethod] = useState<'social' | 'manual' | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [idScanned, setIdScanned] = useState(false);
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [countrySheetOpen, setCountrySheetOpen] = useState(false);
  const fullPhone = `${country.dial} ${phone}`.trim();
  const phoneField = <div className="flex gap-2 mb-2" data-id="element-1824">
      <button type="button" onClick={() => setCountrySheetOpen(true)} className="flex items-center gap-1.5 px-3 py-3 bg-slate-50 border border-slate-200 rounded-2xl active:bg-slate-100 transition-colors flex-shrink-0" data-id="element-1825">
        <img src={country.flag} alt="" className="w-6 h-[18px] rounded-[3px] object-cover ring-1 ring-slate-200" data-id="element-1826" />
        <span className="text-[14px] font-semibold text-slate-700" data-id="element-1827">
          {country.dial}
        </span>
        <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-1828" />
      </button>
      <div className="relative flex-1" data-id="element-1829">
        <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1830" />
        <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="50 123 4567" className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1831" />
      </div>
    </div>;
  const handleSocialLogin = () => {
    setAuthMethod('social');
    setName('Ahmed Al Mansoori');
    setEmail('ahmed@example.com');
    // Even with Google/Apple, we still verify a phone number via OTP.
    setStep('phone');
  };
  const handleManualLogin = (method: 'phone' | 'email') => {
    setAuthMethod('manual');
    setStep(method);
  };
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const goToOtp = () => setStep('otp');
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const otpComplete = otp.every(d => d !== '');
  const stepVariants = {
    enter: {
      opacity: 0,
      x: 24
    },
    center: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -24
    }
  };
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<motion.div initial={{
    y: '100%'
  }} animate={{
    y: 0
  }} exit={{
    y: '100%'
  }} transition={{
    type: 'spring',
    damping: 28,
    stiffness: 250
  }} className="absolute inset-0 z-[200] bg-white flex flex-col overflow-hidden" data-id="element-1832">
      {/* Sticky Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-100 z-20" data-id="element-1833">
        <div className="flex items-center justify-between px-3 py-3" data-id="element-1834">
          <div className="w-10" data-id="element-1835">
            {/* Back button space if needed in header, but we keep them in steps for now */}
          </div>
          <Logo size="sm" data-id="element-1836" />
          <div className="w-10 flex justify-end" data-id="element-1837">
            {onClose && <button onClick={onClose} aria-label="Close" className="p-2 -mr-1 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1838">
                <XIcon className="w-6 h-6" data-id="element-1839" />
              </button>}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden relative" data-id="element-1840">
        <div className="p-6" data-id="element-1841">
          <AnimatePresence mode="wait" data-id="element-1842">
            {/* METHOD SELECTION */}
            {step === 'method' && <motion.div key="method" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1843">
                <div className="text-center mb-7" data-id="element-1844">
                  <h1 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-1845">
                    Welcome to Talab
                  </h1>
                  <p className="text-slate-500 text-sm" data-id="element-1846">
                    Sign in to let your AI agent find anything for you.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-1847">
                  <button onClick={handleSocialLogin} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1848">
                    <GoogleMark data-id="element-1849" />
                    Continue with Google
                  </button>
                  <button onClick={handleSocialLogin} className="w-full flex items-center justify-center gap-3 py-3.5 bg-dark-navy text-white rounded-xl font-medium hover:opacity-90 transition-opacity" data-id="element-1850">
                    <AppleMark data-id="element-1851" />
                    Continue with Apple
                  </button>

                  <div className="flex items-center gap-3 py-2" data-id="element-1852">
                    <div className="flex-1 h-px bg-slate-100" data-id="element-1853" />
                    <span className="text-xs text-slate-400 font-medium" data-id="element-1854">
                      OR
                    </span>
                    <div className="flex-1 h-px bg-slate-100" data-id="element-1855" />
                  </div>

                  <button onClick={() => handleManualLogin('phone')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1856">
                    <PhoneIcon className="w-5 h-5 text-primary-indigo" data-id="element-1857" />
                    Continue with Phone
                  </button>
                  <button onClick={() => handleManualLogin('email')} className="w-full flex items-center justify-center gap-3 py-3.5 border border-slate-200 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1858">
                    <MailIcon className="w-5 h-5 text-primary-indigo" data-id="element-1859" />
                    Continue with Email
                  </button>
                </div>

                <p className="text-center text-xs text-slate-400 mt-6 leading-relaxed" data-id="element-1860">
                  By continuing you agree to Talab's Terms of Service and
                  Privacy Policy.
                </p>
              </motion.div>}

            {/* EMAIL STEP */}
            {step === 'email' && <motion.div key="email" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1861">
                <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-1862">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-1863" />
                  Back
                </button>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1864">
                  Sign in with email
                </h1>
                <p className="text-slate-500 text-sm mb-6" data-id="element-1865">
                  We'll also verify your phone number to secure your account.
                </p>

                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1866">
                  Email address
                </label>
                <div className="relative mb-4" data-id="element-1867">
                  <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1868" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1869" />
                </div>

                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1870">
                  Phone number <span className="text-error" data-id="element-1871">*</span>
                </label>
                {phoneField}
                <p className="text-xs text-slate-400 mb-6" data-id="element-1872">
                  Required — we'll send a one-time code to verify it.
                </p>

                <button onClick={goToOtp} disabled={!email.includes('@') || phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1873">
                  Send verification code
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1874" />
                </button>
              </motion.div>}

            {/* PHONE STEP */}
            {step === 'phone' && <motion.div key="phone" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1875">
                <button onClick={() => setStep('method')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-1876">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-1877" />
                  Back
                </button>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1878">
                  Verify your phone
                </h1>
                <p className="text-slate-500 text-sm mb-6" data-id="element-1879">
                  Enter your phone number and we'll send you a one-time code.
                </p>

                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1880">
                  Phone number <span className="text-error" data-id="element-1881">*</span>
                </label>
                {phoneField}
                <div className="mb-6" data-id="element-1882" />

                <button onClick={goToOtp} disabled={phone.replace(/\D/g, '').length < 7} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1883">
                  Send verification code
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1884" />
                </button>
              </motion.div>}

            {/* OTP STEP */}
            {step === 'otp' && <motion.div key="otp" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1885">
                <button onClick={() => setStep(authMethod === 'manual' && email ? 'email' : 'phone')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary-indigo mb-5 transition-colors" data-id="element-1886">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-1887" />
                  Back
                </button>
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1888">
                  Enter verification code
                </h1>
                <p className="text-slate-500 text-sm mb-3" data-id="element-1889">
                  We sent a 6-digit code via WhatsApp to{' '}
                  <span className="font-medium text-slate-700" data-id="element-1890">
                    {phone ? fullPhone : 'your phone'}
                  </span>
                  .
                </p>
                <div className="inline-flex items-center gap-1.5 mb-6 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-full" data-id="element-1891">
                  <MessageCircleIcon className="w-3.5 h-3.5 text-emerald-600" data-id="element-1892" />
                  <span className="text-[11px] font-semibold text-emerald-700" data-id="element-1893">
                    Sent on WhatsApp
                  </span>
                </div>

                <div className="flex justify-between gap-2 mb-6" data-id="element-1894">
                  {otp.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} aria-label={`Digit ${i + 1}`} className="w-12 h-14 text-center text-xl font-bold border border-slate-200 rounded-xl outline-none focus:border-primary-indigo focus:ring-2 focus:ring-primary-indigo/20 transition-all" data-id="element-1895" />)}
                </div>

                <button onClick={() => setStep('info')} disabled={!otpComplete} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1896">
                  Verify & continue
                </button>

                <p className="text-center text-sm text-slate-500 mt-5" data-id="element-1897">
                  Didn't get the code?{' '}
                  <button className="font-medium text-primary-indigo hover:underline" data-id="element-1898">
                    Resend
                  </button>
                </p>
              </motion.div>}

            {/* INFO STEP */}
            {step === 'info' && <motion.div key="info" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1899">
                <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1900">
                  Complete your profile
                </h1>
                <p className="text-slate-500 text-sm mb-6" data-id="element-1901">
                  {authMethod === 'social' ? 'Please confirm the details provided by your account.' : 'Please enter your details to complete your account.'}
                </p>

                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1902">
                  Full name <span className="text-error" data-id="element-1903">*</span>
                </label>
                <div className="relative mb-4" data-id="element-1904">
                  <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1905" />
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Ahmed Al Mansoori" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1906" />
                </div>

                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1907">
                  Email address <span className="text-error" data-id="element-1908">*</span>
                </label>
                <div className="relative mb-6" data-id="element-1909">
                  <MailIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-1910" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:border-primary-indigo/40 transition-colors" data-id="element-1911" />
                </div>

                <button onClick={() => setStep('id_scan')} disabled={!name.trim() || !email.includes('@')} className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1912">
                  Continue
                  <ArrowRightIcon className="w-4 h-4" data-id="element-1913" />
                </button>
              </motion.div>}

            {/* ID SCAN STEP */}
            {step === 'id_scan' && <motion.div key="id_scan" variants={stepVariants} initial="enter" animate="center" exit="exit" data-id="element-1914">
                <div className="text-center mb-6" data-id="element-1915">
                  <div className="w-16 h-16 bg-primary-indigo/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1916">
                    <ShieldCheckIcon className="w-8 h-8 text-primary-indigo" data-id="element-1917" />
                  </div>
                  <h1 className="font-display font-bold text-xl text-dark-navy mb-2" data-id="element-1918">
                    Verify your identity
                  </h1>
                  <p className="text-slate-500 text-sm" data-id="element-1919">
                    Scan your Emirates ID to get a verified badge and build
                    trust with buyers. This is optional.
                  </p>
                </div>

                <div className="mb-8" data-id="element-1920">
                  <button onClick={() => setIdScanned(true)} className={`w-full flex flex-col items-center justify-center gap-3 py-8 border-2 border-dashed rounded-2xl transition-colors ${idScanned ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-300 bg-slate-50 hover:border-primary-indigo/50 hover:bg-primary-indigo/5'}`} data-id="element-1921">
                    {idScanned ? <>
                        <CheckCircle2Icon className="w-8 h-8 text-emerald-500" data-id="element-1922" />
                        <span className="font-medium text-emerald-700" data-id="element-1923">
                          ID Verified Successfully
                        </span>
                      </> : <>
                        <CameraIcon className="w-8 h-8 text-slate-400" data-id="element-1924" />
                        <span className="font-medium text-slate-600" data-id="element-1925">
                          Tap to scan Emirates ID
                        </span>
                      </>}
                  </button>
                </div>

                <div className="space-y-3" data-id="element-1926">
                  <button onClick={() => onLogin(idScanned)} className="w-full py-3.5 bg-primary-indigo text-white rounded-xl font-medium hover:bg-primary-indigo/90 transition-colors" data-id="element-1927">
                    Complete setup
                  </button>
                  <button onClick={() => onLogin(false)} className="w-full py-3.5 text-slate-500 font-medium hover:text-slate-700 transition-colors" data-id="element-1928">
                    Skip for now
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Country selector sheet */}
      <AnimatePresence data-id="element-1929">
        {countrySheetOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 z-[60] flex flex-col justify-end" data-id="element-1930">
            <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-[2px]" onClick={() => setCountrySheetOpen(false)} data-id="element-1931" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="relative bg-white rounded-t-3xl pb-7 pt-3 max-h-[70%] flex flex-col" data-id="element-1932">
              <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-3" data-id="element-1933" />
              <h3 className="px-6 text-[15px] font-bold text-dark-navy mb-1" data-id="element-1934">
                Select country
              </h3>
              <p className="px-6 text-[12px] text-slate-400 mb-3" data-id="element-1935">
                More countries are coming soon.
              </p>
              <div className="overflow-y-auto px-3" data-id="element-1936">
                {COUNTRIES.map(c => {
              const active = c.code === country.code;
              return <button key={c.code} disabled={!c.available} onClick={() => {
                if (!c.available) return;
                setCountry(c);
                setCountrySheetOpen(false);
              }} className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl transition-colors ${active ? 'bg-primary-indigo/5' : c.available ? 'active:bg-slate-50' : 'opacity-60'}`} data-id="element-1937">
                      <img src={c.flag} alt="" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-slate-200" data-id="element-1938" />
                      <span className="flex-1 text-left min-w-0" data-id="element-1939">
                        <span className="block text-[14px] font-semibold text-dark-navy truncate" data-id="element-1940">
                          {c.name}
                        </span>
                        <span className="block text-[12px] text-slate-400" data-id="element-1941">
                          {c.dial}
                        </span>
                      </span>
                      {active ? <CheckIcon className="w-5 h-5 text-primary-indigo flex-shrink-0" data-id="element-1942" /> : !c.available ? <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-lg" data-id="element-1943">
                          Soon
                        </span> : null}
                    </button>;
            })}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>, mountNode);
}